import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Users, GraduationCap, Car, CalendarCheck, Clock } from "lucide-react";
import { formatDate } from "@/lib/format";
import { AdminInvitations } from "@/components/AdminInvitations";
import { AdminInstructors } from "@/components/AdminInstructors";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/users"), api.get("/bookings")])
      .then(([s, u, b]) => { setStats(s.data); setUsers(u.data); setBookings(b.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-arctic-primary" /></div></div>;

  const cards = [
    { label: "Students", value: stats.students, icon: GraduationCap, color: "text-emerald-600 bg-emerald-50" },
    { label: "Instructors", value: stats.instructors, icon: Car, color: "text-sky-600 bg-sky-50" },
    { label: "Total bookings", value: stats.total_bookings, icon: CalendarCheck, color: "text-arctic-primary bg-arctic-primary/10" },
    { label: "Pending", value: stats.pending, icon: Clock, color: "text-amber-600 bg-amber-50" },
    { label: "Completed", value: stats.completed, icon: Users, color: "text-indigo-600 bg-indigo-50" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading font-extrabold text-3xl text-arctic-secondary tracking-tight" data-testid="dashboard-title">Admin overview</h1>
        <p className="text-muted-foreground mt-1">Full visibility across Arctic Driving School.</p>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
          {cards.map((c) => (
            <Card key={c.label} className="p-5 rounded-2xl border-border">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${c.color}`}><c.icon className="w-5 h-5" /></div>
              <div className="font-heading font-extrabold text-2xl text-arctic-secondary">{c.value}</div>
              <div className="text-sm text-muted-foreground">{c.label}</div>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="bookings" className="mt-8">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">All bookings</TabsTrigger>
            <TabsTrigger value="invitations" data-testid="admin-tab-invitations">Invitations</TabsTrigger>
            <TabsTrigger value="instructors" data-testid="admin-tab-instructors">Instructors</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings" className="mt-6">
            <Card className="rounded-2xl border-border overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Instructor</TableHead><TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {bookings.map((b) => (
                    <TableRow key={b.booking_id} data-testid={`admin-booking-${b.booking_id}`}>
                      <TableCell>{b.student_name}</TableCell>
                      <TableCell>{b.instructor_name}</TableCell>
                      <TableCell>{formatDate(b.date)}</TableCell>
                      <TableCell>{b.start_time}–{b.end_time}</TableCell>
                      <TableCell><StatusBadge status={b.status} /></TableCell>
                    </TableRow>
                  ))}
                  {bookings.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No bookings yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
          <TabsContent value="users" className="mt-6">
            <Card className="rounded-2xl border-border overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Provider</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell className="capitalize">{u.role || "—"}</TableCell>
                      <TableCell className="capitalize">{u.auth_provider}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
          <TabsContent value="invitations" className="mt-6">
            <AdminInvitations />
          </TabsContent>
          <TabsContent value="instructors" className="mt-6">
            <AdminInstructors />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
